/**
 * Escapes nested quotes within HTML attribute values to prevent parsing errors.
 * Specifically designed for CKEditor-generated placeholder HTML with double-quoted attributes.
 *
 * @param {string} html - The HTML string to escape
 * @returns {string} HTML with properly escaped attribute quotes
 *
 * @example
 * // Input:  <span rel="{x."car.name"}">Text</span>
 * // Output: <span rel="{x.&quot;car.name&quot;}">Text</span>
 *
 * @note Assumptions and behavior:
 * - Assumes well-formed HTML structure (CKEditor-generated)
 * - Already-escaped quotes (&quot;) in input will not be double-escaped
 */
const escapeNestedQuotes = html => {
    // Validate input
    if (typeof html !== 'string') {
        return html;
    }

    if (!html) {
        return html;
    }

    let result = '';
    let i = 0;

    while (i < html.length) {
        // Look for attribute assignments (including hyphenated attributes like data-*, aria-*)
        const attrMatch = html.slice(i).match(/^([\w-]+)="/);

        if (attrMatch) {
            // Found an attribute, add it to result
            result += attrMatch[0];
            i += attrMatch[0].length;

            // Now find the closing quote, escaping any quotes in between
            let attrValue = '';

            while (i < html.length) {
                const char = html[i];

                if (char === '"') {
                    // Check if this is the closing quote by looking at what follows:
                    // - End of string
                    // - Whitespace (space, tab, newline) or tag closing (>, />)
                    const nextChar = html[i + 1];

                    if (!nextChar || /[\s>/]/.test(nextChar)) {
                        // This is the closing quote
                        result += attrValue + '"';
                        i++;
                        break;
                    }

                    // Check if followed by start of next attribute (e.g., attr="value")
                    // Using 20 chars for safety to handle long attribute names
                    const nextChunk = html.slice(i + 1, i + 21);
                    if (/^[\w-]+="/.test(nextChunk)) {
                        // This is the closing quote
                        result += attrValue + '"';
                        i++;
                        break;
                    }

                    // Otherwise, this is a nested quote - escape it
                    attrValue += '&quot;';
                    i++;
                } else {
                    attrValue += char;
                    i++;
                }
            }

            // Handle unclosed attribute (malformed HTML)
            // Always add closing quote if we started parsing an attribute value
            if (i >= html.length) {
                result += attrValue + '"';
            }
        } else {
            // Not an attribute, just copy the character
            result += html[i];
            i++;
        }
    }

    return result;
};

/**
 * Inserts content at a specific position in a CKEditor instance
 * Handles both rich text and plain text modes, managing proper content insertion
 *
 * @param {Object} cke - CKEditor instance
 * @param {string} contentToInsert - The content to be inserted
 * @param {Array} position - Array containing path coordinates [line, offset]
 * @returns {void}
 */
export const insertContentAtPosition = (
    cke,
    contentToInsert,
    position = []
) => {
    if (!cke || !contentToInsert) return;

    setTimeout(() => {
        const root = cke.model.document.getRoot();

        // Handle default empty position array
        if (!position.length) {
            position = [0, 0];
        }

        // Ensure the path doesn't exceed valid bounds
        let safePath0 = position[0];
        let safePath1 = Math.min(
            position[1],
            root.getChild(safePath0)?.maxOffset || 0
        );

        if (position[0] > root.childCount - 1) {
            safePath0 = root.childCount - 1;
            // Try setting path1 to end of the last line
            safePath1 = root.getChild(safePath0)?.maxOffset || 0;
        }

        const pos = cke.model.createPositionFromPath(root, [
            safePath0,
            safePath1
        ]);

        // Escape nested quotes in attribute values to prevent parser errors
        const sanitizedContent = escapeNestedQuotes(contentToInsert);

        const viewFragment = cke.data.processor.toView(sanitizedContent);
        const modelFragment = cke.data.toModel(viewFragment);

        if (modelFragment) {
            cke.model.insertContent(modelFragment, pos);
            if (cke.plainTextIsActive) {
                // Remove newline as placeholder is added with new line when plain text is active
                cke.data.set(cke.getData().replace(/\n+(?=<span)/g, ''));
            }
        }
    }, 500);
};

/**
 * Gets the CKEditor instance from a field by its ID
 *
 * @param {string} fieldId - The ID of the field containing the CKEditor
 * @returns {Object|null} The CKEditor instance or null if not found
 */
export const getCKEditorInstanceFromField = fieldId => {
    if (!fieldId) return null;

    const domEditableElement = document
        .getElementById(fieldId)
        ?.querySelector('.ck-editor__editable');

    return domEditableElement?.ckeditorInstance || null;
};
